import { useEffect, useMemo, useState, useImperativeHandle, forwardRef } from 'react';
import { isEqual } from 'lodash-es';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import type {
  stt_provider,
  TypeSTTConfigForm,
} from '@intone/shared';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfigResetButtons } from '@/components/ConfigResetButtons';
import {
  FormSelectField,
} from '@/components/form-fields';

import { useSttConfigs, useSaveSttConfig } from '../model/useSttConfigs';
import { mapToForm } from '../model/mapToForm';

import { AzureAsrFields } from './AzureAsrFields';
import { AzureOpenaiFields } from './AzureOpenaiFields';
import { DeepgramFields } from './DeepgramFields';
import { GoogleFields } from './GoogleFields';

interface STTConfigurationBlockProps {
  agentId: string;
  selectedId: number;
  onHasChanges?: (hasChanges: boolean) => void;
  readOnly?: boolean;
  headerRight?: React.ReactNode;
}

export interface STTConfigurationBlockRef {
  save: () => Promise<boolean>;
  hasChanges: () => boolean;
}

const PROVIDER_OPTIONS = [
  { value: 'deepgram', label: 'Deepgram' },
  { value: 'azure_asr', label: 'Azure ASR' },
  { value: 'azure_openai', label: 'Azure OpenAI' },
  { value: 'google', label: 'Google' },
];

export const STTConfigurationBlock = forwardRef<
  STTConfigurationBlockRef,
  STTConfigurationBlockProps
>(function STTConfigurationBlock({ agentId, selectedId, onHasChanges, readOnly, headerRight }, ref) {
  const queryClient = useQueryClient();
  const { data: sttConfigs, isSuccess, isPending, isFetching } = useSttConfigs(selectedId);
  const saveConfig = useSaveSttConfig();

  const defaultConfig: TypeSTTConfigForm = useMemo(
    () => mapToForm(sttConfigs?.find(c => c.id === 1)),
    [sttConfigs]
  );

  const selectedConfig = useMemo(
    () => mapToForm(sttConfigs?.find(c => c.id === selectedId)),
    [sttConfigs, selectedId]
  );

  const [currentConfig, setCurrentConfig] = useState<TypeSTTConfigForm>(mapToForm());

  useEffect(() => {
    if (isSuccess && !isFetching) {
      setCurrentConfig(selectedConfig);
    }
  }, [selectedId, isSuccess, isFetching]);

  const hasChanges = useMemo(() => !isEqual(currentConfig, selectedConfig), [currentConfig, selectedConfig]);

  useEffect(() => {
    onHasChanges?.(hasChanges);
  }, [hasChanges, onHasChanges]);

  const updateConfig = (field: keyof TypeSTTConfigForm, value: any) => {
    if (field === 'provider') {
      setCurrentConfig((prev: TypeSTTConfigForm) => ({
        ...prev,
        model: value === 'deepgram' ? 'nova-3' : (value === 'azure_openai' ? 'gpt-4o-mini-transcribe' : 'telephony'),
        detect_language: value === 'google',
        [field]: value,
      }));
      return;
    }

    setCurrentConfig((prev: TypeSTTConfigForm) => ({
      ...prev,
      [field]: value,
    }));
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!hasChanges) return true;

      if (!currentConfig.name) {
        toast.error('STT: Config name is required');
        return false;
      }

      return new Promise((resolve) => {
        saveConfig.mutate(
          {
            id: selectedId === 1 ? undefined : selectedId,
            agentId,
            ...currentConfig,
          },
          {
            onSuccess: () => {
              if (selectedId === 1) {
                queryClient.invalidateQueries({ queryKey: ['engineAgent', agentId] });
              }
              resolve(true);
            },
            onError: () => {
              resolve(false);
            },
          }
        );
      });
    },
    hasChanges: () => hasChanges,
  }), [hasChanges, currentConfig, selectedId, agentId, saveConfig, queryClient]);

  if (isPending) {
    return null;
  }

  return (
    <Card className={`border-gray-300 ${isFetching ? 'opacity-70' : ''}`}>
      <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-body font-semibold">Speech-to-Text Configuration</CardTitle>
          <div className="flex items-center gap-2">
            {!readOnly && (
              <ConfigResetButtons
                hasChanges={hasChanges}
                showRestore={selectedId !== 1}
                isRestoreDisabled={isEqual(currentConfig, defaultConfig)}
                onReset={() => setCurrentConfig(selectedConfig)}
                onRestoreDefault={() => setCurrentConfig(defaultConfig)}
              />
            )}
            {headerRight}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6 grid gap-4">
        <div className={`space-y-4 ${readOnly ? 'opacity-70 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-2 gap-4">
            <FormSelectField
              id="stt-provider"
              label="Provider"
              value={currentConfig.provider}
              onChange={(v) => updateConfig('provider', v as stt_provider)}
              options={PROVIDER_OPTIONS}
              required
            />
          </div>

          {currentConfig.provider === 'deepgram' && (
            <DeepgramFields currentConfig={currentConfig} updateConfig={updateConfig} />
          )}
          {currentConfig.provider === 'azure_asr' && (
            <AzureAsrFields currentConfig={currentConfig} updateConfig={updateConfig} />
          )}
          {currentConfig.provider === 'azure_openai' && (
            <AzureOpenaiFields currentConfig={currentConfig} updateConfig={updateConfig} />
          )}
          {currentConfig.provider === 'google' && (
            <GoogleFields currentConfig={currentConfig} updateConfig={updateConfig} />
          )}
        </div>
      </CardContent>
    </Card>
  );
});
